import React, { useState, useEffect, useRef } from 'react';
import { db } from '../../firebase';
import { doc, updateDoc } from 'firebase/firestore';
import { LOOTBOX_RARITIES, RARITY_TIERS_REVERSED } from '../../constants/lootbox';
import { VFX_EFFECTS } from '../../constants/vfx';
import { VfxSpan } from '../../utils/helpers';
import { useToast } from '../ToastContext';
import ItemIcon from './ItemIcon';

function LootboxInventory({ inventory, user, userData, onUserDataChange }) {
  const toast = useToast();
  const [statTrakTooltip, setStatTrakTooltip] = useState(null); // { item, x, y }
  const [vfxTooltip, setVfxTooltip] = useState(null); // { item, x, y }
  const repairedRef = useRef(false);

  // Auto-repair: if equipped emoji is missing VFX but the inventory item has it, update Firestore
  useEffect(() => {
    if (repairedRef.current || !user || !userData?.equippedEmoji || !inventory.length) return;
    const eq = userData.equippedEmoji;
    if (eq.vfx && eq.vfx.length > 0) return; // already has VFX, nothing to repair

    // Find the matching inventory item by unboxedAt/obtainedAt + id
    const match = inventory.find(item =>
      item.id === eq.id &&
      (item.unboxedAt || item.obtainedAt) === (eq.unboxedAt || eq.obtainedAt) &&
      item.vfx && item.vfx.length > 0
    );
    if (!match) return;

    // Repair: write the missing vfx field to the equipped emoji in Firestore
    repairedRef.current = true;
    const updatedEmoji = { ...eq, vfx: match.vfx };
    updateDoc(doc(db, "users", user.uid), { equippedEmoji: updatedEmoji })
      .then(() => onUserDataChange())
      .catch(() => {}); // best-effort
  }, [user, userData, inventory, onUserDataChange]);

  if (inventory.length === 0) {
    return (
      <div className="empty-state">
        <div className="empty-icon">🎒</div>
        <h2>Empty Inventory</h2>
        <p>Open lootboxes to collect rewards!</p>
      </div>
    );
  }

  // Handler to equip an emoji
  async function handleEquipEmoji(item) {
    if (!user) return;

    try {
      const equippedData = {
        id: item.id,
        name: item.name,
        icon: item.icon,
        rarity: item.rarity,
        isShiny: item.isShiny || false,
        unboxedAt: item.unboxedAt || item.obtainedAt // Store to match inventory item
      };

      // Add VFX effects if item has them
      if (item.vfx && item.vfx.length > 0) {
        equippedData.vfx = item.vfx;
      }

      // Add StatTrak properties if item is StatTrak
      if (item.isStatTrak) {
        equippedData.isStatTrak = true;
        equippedData.trackedStats = item.trackedStats;
        equippedData.statValues = item.statValues || {};
      }

      await updateDoc(doc(db, "users", user.uid), {
        equippedEmoji: equippedData
      });
      await onUserDataChange();
      // Emoji will be reloaded on next page load
    } catch (error) {
      console.error("Error equipping emoji:", error);
      toast.error("Failed to equip emoji. Please try again.");
    }
  }

  // Handler to unequip emoji
  async function handleUnequipEmoji() {
    if (!user) return;

    try {
      await updateDoc(doc(db, "users", user.uid), {
        equippedEmoji: null
      });
      await onUserDataChange();
      // Emoji will be reloaded on next page load
    } catch (error) {
      console.error("Error unequipping emoji:", error);
      toast.error("Failed to unequip emoji. Please try again.");
    }
  }

  // Group by rarity — StatTrak items always show as individual entries (never stacked)
  const grouped = {};

  inventory.forEach(item => {
    if (!grouped[item.rarity]) {
      grouped[item.rarity] = {};
    }

    // StatTrak and VFX items are unique per instance (include unboxedAt so they never merge)
    // Non-StatTrak/non-VFX items stack by id + shiny status
    const shinyStr = item.isShiny ? '_shiny' : '';
    const key = (item.isStatTrak || (item.vfx && item.vfx.length > 0))
      ? `${item.id}_${item.unboxedAt || item.obtainedAt}`
      : `${item.id}${shinyStr}`;

    if (!grouped[item.rarity][key]) {
      grouped[item.rarity][key] = { ...item, count: 0 };
    }
    grouped[item.rarity][key].count++;
  });

  const equippedEmoji = userData?.equippedEmoji;

  return (
    <div className="inventory-container">
      {/* Equipped Emoji Display */}
      {userData?.equippedEmoji && (
        <div className="equipped-emoji-display">
          <h3>Currently Equipped</h3>
          <div className="equipped-emoji-card">
            <div className="equipped-emoji-icon">
              {userData.equippedEmoji.vfx && userData.equippedEmoji.vfx.length > 0
                ? <VfxSpan vfx={userData.equippedEmoji.vfx}><ItemIcon item={userData.equippedEmoji} size={48} /></VfxSpan>
                : <ItemIcon item={userData.equippedEmoji} size={48} />}
            </div>
            <div className="equipped-emoji-name">
              {userData.equippedEmoji.vfx && userData.equippedEmoji.vfx.length > 0
                ? <VfxSpan vfx={userData.equippedEmoji.vfx}>{userData.equippedEmoji.name}</VfxSpan>
                : userData.equippedEmoji.name}
              {userData.equippedEmoji.isShiny && <span style={{ marginLeft: '8px' }}>✨</span>}
              {userData.equippedEmoji.isStatTrak && <span style={{ marginLeft: '8px', color: '#FF8C00', fontWeight: 'bold' }}>ST™</span>}
              {userData.equippedEmoji.vfx && userData.equippedEmoji.vfx.length > 0 && (
                <span style={{ marginLeft: '8px', color: '#a78bfa', fontWeight: 'bold', fontSize: '13px' }}>
                  ✦ {userData.equippedEmoji.vfx.length > 1 ? `VFX x${userData.equippedEmoji.vfx.length}` : 'VFX'}
                </span>
              )}
            </div>

            {/* StatTrak Stats Display */}
            {userData.equippedEmoji.isStatTrak && userData.equippedEmoji.trackedStats && (
              <div style={{
                marginTop: '16px',
                padding: '12px',
                background: 'rgba(255, 140, 0, 0.1)',
                border: '1px solid rgba(255, 140, 0, 0.3)',
                borderRadius: '8px'
              }}>
                <h4 style={{ color: '#FF8C00', fontSize: '14px', marginBottom: '8px' }}>📊 Tracked Stats</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  {userData.equippedEmoji.trackedStats.map(stat => {
                    const value = userData.equippedEmoji.statValues?.[stat] || 0;
                    const statName = stat.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                    return (
                      <div key={stat} style={{
                        fontSize: '12px',
                        padding: '4px 8px',
                        background: 'rgba(0, 0, 0, 0.2)',
                        borderRadius: '4px'
                      }}>
                        <div style={{ color: '#888', fontSize: '10px' }}>{statName}</div>
                        <div style={{ color: '#FF8C00', fontWeight: 'bold', fontSize: '14px' }}>{value}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* VFX Effects Display */}
            {userData.equippedEmoji.vfx && userData.equippedEmoji.vfx.length > 0 && (
              <div style={{
                marginTop: '16px',
                padding: '12px',
                background: 'rgba(167, 139, 250, 0.08)',
                border: '1px solid rgba(167, 139, 250, 0.3)',
                borderRadius: '8px'
              }}>
                <h4 style={{ color: '#a78bfa', fontSize: '14px', marginBottom: '8px' }}>✦ VFX Effects</h4>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {(Array.isArray(userData.equippedEmoji.vfx) ? userData.equippedEmoji.vfx : [userData.equippedEmoji.vfx]).map((effectId, idx) => {
                    const effect = VFX_EFFECTS.find(e => e.id === effectId);
                    return (
                      <div key={idx} style={{
                        padding: '4px 10px',
                        background: 'rgba(167, 139, 250, 0.15)',
                        border: '1px solid rgba(167, 139, 250, 0.4)',
                        borderRadius: '12px',
                        fontSize: '12px',
                        color: '#c4b5fd',
                        fontWeight: 'bold'
                      }}>
                        {effect ? effect.name : effectId}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <button className="btn-unequip" onClick={handleUnequipEmoji}>
              Unequip
            </button>
          </div>
        </div>
      )}

      {RARITY_TIERS_REVERSED.map(rarityKey => {
        const rarityData = LOOTBOX_RARITIES[rarityKey];
        const itemsOfRarity = grouped[rarityKey] || {};
        const uniqueItems = Object.values(itemsOfRarity);
        if (uniqueItems.length === 0) return null;

        const totalCount = uniqueItems.reduce((sum, item) => sum + item.count, 0);

        return (
          <div key={rarityKey} className="inventory-section">
            <h3 style={{ color: rarityData.color }}>
              {rarityData.name} ({totalCount})
            </h3>
            <div className="inventory-grid">
              {uniqueItems.map((item, index) => {
                // Check if this specific item instance is equipped by matching id, rarity, shiny status, AND unboxedAt timestamp
                const isEquipped = equippedEmoji &&
                  item.id === equippedEmoji.id &&
                  item.rarity === equippedEmoji.rarity &&
                  (item.isShiny || false) === (equippedEmoji.isShiny || false) &&
                  (item.unboxedAt || item.obtainedAt) === (equippedEmoji.unboxedAt || equippedEmoji.obtainedAt);
                const isShinyGalaxy = item.isShiny && item.rarity === 'GALAXY';
                const isNuclear = item.rarity === 'NUCLEAR';
                return (
                  <div
                    key={index}
                    className={`inventory-item ${rarityData.glow ? 'glow' : ''} ${isEquipped ? 'equipped' : ''} ${isNuclear ? 'nuclear' : isShinyGalaxy ? 'shiny-galaxy' : item.isShiny ? 'shiny' : ''}`}
                    style={{
                      borderColor: isNuclear || isShinyGalaxy ? 'transparent' : rarityData.color,
                      boxShadow: isNuclear || isShinyGalaxy
                        ? ''
                        : (rarityData.glow ? `0 0 15px ${rarityData.color}` : 'none')
                    }}
                    onMouseEnter={(e) => {
                      if (item.isStatTrak) setStatTrakTooltip({ item, x: e.clientX, y: e.clientY });
                      if (item.vfx && item.vfx.length > 0) setVfxTooltip({ item, x: e.clientX, y: e.clientY });
                    }}
                    onMouseMove={(e) => {
                      if (item.isStatTrak) setStatTrakTooltip(t => t ? { ...t, x: e.clientX, y: e.clientY } : null);
                      if (item.vfx && item.vfx.length > 0) setVfxTooltip(t => t ? { ...t, x: e.clientX, y: e.clientY } : null);
                    }}
                    onMouseLeave={() => { setStatTrakTooltip(null); setVfxTooltip(null); }}
                  >
                    {isNuclear ? (
                      <div className="nuclear-badge">☢️ NUCLEAR ☢️</div>
                    ) : isShinyGalaxy ? (
                      <div className="shiny-galaxy-badge">🌌 GALAXY ✨</div>
                    ) : item.isShiny && (
                      <div className="shiny-badge-small">✨</div>
                    )}
                    {item.isStatTrak && (
                      <div className="stattrak-badge-small" style={{
                        position: 'absolute',
                        top: '4px',
                        right: '4px',
                        fontSize: '16px',
                        background: 'rgba(255, 140, 0, 0.9)',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        fontWeight: 'bold',
                        color: '#000'
                      }}>
                        ST
                      </div>
                    )}
                    <div className="item-icon">
                      {item.vfx
                        ? <VfxSpan vfx={item.vfx} style={{ display: 'inline-block', fontSize: 'inherit' }}><ItemIcon item={item} size={48} /></VfxSpan>
                        : <ItemIcon item={item} size={48} />}
                    </div>
                    <div className={`item-name ${rarityData.rainbow && !item.isShiny ? 'rainbow-glow' : ''}`} style={{ color: item.isShiny ? '#FFD700' : rarityData.color }}>
                      {item.vfx
                        ? <VfxSpan vfx={item.vfx}>{item.name}</VfxSpan>
                        : item.name}
                      {item.isStatTrak && <span style={{ marginLeft: '4px', fontSize: '12px', color: '#FF8C00' }}>™</span>}
                      {item.vfx && item.vfx.length > 0 && <span style={{ marginLeft: '4px', fontSize: '10px', color: '#a78bfa', fontWeight: 'bold' }}>✦ VFX{Array.isArray(item.vfx) && item.vfx.length > 1 ? ` x${item.vfx.length}` : ''}</span>}
                    </div>
                    {!item.isStatTrak && item.count > 1 && (
                      <div className="item-count">
                        x{item.count}
                      </div>
                    )}
                    <div className="item-date">
                      {new Date(item.unboxedAt || item.obtainedAt).toLocaleDateString()}
                    </div>
                    {!isEquipped && (
                      <button
                        className="btn-equip"
                        onClick={() => handleEquipEmoji(item)}
                      >
                        Equip
                      </button>
                    )}
                    {isEquipped && (
                      <div className="equipped-badge">✓ Equipped</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* StatTrak hover tooltip */}
      {statTrakTooltip && (
        <div style={{
          position: 'fixed',
          left: statTrakTooltip.x + 14,
          top: statTrakTooltip.y + 14,
          zIndex: 9999,
          background: 'rgba(20,20,20,0.97)',
          border: '2px solid #FF8C00',
          borderRadius: '10px',
          padding: '12px 16px',
          pointerEvents: 'none',
          minWidth: '180px',
          boxShadow: '0 4px 24px rgba(255,140,0,0.3)'
        }}>
          <div style={{ color: '#FF8C00', fontWeight: 'bold', fontSize: '13px', marginBottom: '8px' }}>
            📊 StatTrak™ Stats
          </div>
          {(statTrakTooltip.item.trackedStats || []).map(stat => {
            const value = statTrakTooltip.item.statValues?.[stat] || 0;
            const label = stat.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
            return (
              <div key={stat} style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', fontSize: '12px', marginBottom: '4px' }}>
                <span style={{ color: '#aaa' }}>{label}</span>
                <span style={{ color: '#FF8C00', fontWeight: 'bold' }}>{value}</span>
              </div>
            );
          })}
          {(!statTrakTooltip.item.trackedStats || statTrakTooltip.item.trackedStats.length === 0) && (
            <div style={{ color: '#666', fontSize: '12px' }}>No stats tracked yet</div>
          )}
        </div>
      )}
      {vfxTooltip && (
        <div style={{
          position: 'fixed',
          left: vfxTooltip.x + 14,
          top: vfxTooltip.y + (statTrakTooltip ? 120 : 14),
          zIndex: 9999,
          background: 'rgba(20,20,20,0.97)',
          border: '2px solid #a78bfa',
          borderRadius: '10px',
          padding: '12px 16px',
          pointerEvents: 'none',
          minWidth: '160px',
          boxShadow: '0 4px 24px rgba(167,139,250,0.3)'
        }}>
          <div style={{ color: '#a78bfa', fontWeight: 'bold', fontSize: '13px', marginBottom: '8px' }}>✦ VFX Effects</div>
          {(Array.isArray(vfxTooltip.item.vfx) ? vfxTooltip.item.vfx : [vfxTooltip.item.vfx]).map((effectId, idx) => {
            const effect = VFX_EFFECTS.find(e => e.id === effectId);
            return (
              <div key={idx} style={{ fontSize: '12px', marginBottom: '4px', color: '#c4b5fd' }}>
                <span style={{ fontWeight: 'bold' }}>{effect ? effect.name : effectId}</span>
                {effect && <span style={{ color: '#888', marginLeft: '6px' }}>— {effect.description}</span>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default LootboxInventory;
